/**
 * Leviathan - Customer Type Template Form Page
 * Quasar Prime LLC
 *
 * Create/Edit customer type template with product management
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';
import { API_URL } from '@/config/api';
import { Loader2, Plus, Trash2, ArrowLeft, Save } from 'lucide-react';

interface TemplateProduct {
  id: string;
  productId: string;
  defaultMargin: number | null;
  product: {
    id: string;
    name: string;
    sku: string;
  };
}

interface InventoryItem {
  id: string;
  name: string;
  sku: string;
}

interface FormData {
  name: string;
  description: string;
  isActive: boolean;
}

export default function CustomerTypeTemplateFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    isActive: true,
  });

  const [products, setProducts] = useState<TemplateProduct[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add product form state
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [productMargin, setProductMargin] = useState('');
  const [addingProduct, setAddingProduct] = useState(false);

  useEffect(() => {
    fetchInventoryItems();
    if (isEdit) {
      fetchTemplate();
    } else {
      setLoading(false);
    }
  }, [id, accessToken]);

  const fetchTemplate = async () => {
    if (!accessToken) return;
    try {
      const response = await fetch(
        `${API_URL}/api/customer-type-templates/${id}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      if (!response.ok) throw new Error('Failed to fetch template');
      const data = await response.json();
      const template = data.template;
      setFormData({
        name: template.name,
        description: template.description || '',
        isActive: template.isActive,
      });
      setProducts(template.products || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const fetchInventoryItems = async () => {
    if (!accessToken) return;
    try {
      const response = await fetch(
        `${API_URL}/api/inventory?isActive=true&limit=500`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      if (!response.ok) throw new Error('Failed to fetch products');
      const data = await response.json();
      setInventoryItems(data.items || []);
    } catch (err) {
      console.error('Failed to fetch inventory items:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessToken) return;
    setSaving(true);
    setError(null);

    try {
      const url = isEdit
        ? `${API_URL}/api/customer-type-templates/${id}`
        : `${API_URL}/api/customer-type-templates`;
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description || null,
          isActive: formData.isActive,
        }),
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (!isEdit) {
        // Navigate to edit page so user can add products
        navigate(`/admin/customer-type-templates/${data.template.id}`, {
          replace: true,
        });
      } else {
        // Stay on page, show success briefly
        setError(null);
        fetchTemplate();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleAddProduct = async () => {
    if (!accessToken || !selectedProductId || !id) return;
    setAddingProduct(true);

    try {
      const response = await fetch(
        `${API_URL}/api/customer-type-templates/${id}/products`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            productId: selectedProductId,
            defaultMargin: productMargin ? parseFloat(productMargin) : null,
          }),
        }
      );

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setProducts((prev) => [...prev, data.product]);
      setSelectedProductId('');
      setProductMargin('');
      setShowAddProduct(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to add product');
    } finally {
      setAddingProduct(false);
    }
  };

  const handleRemoveProduct = async (templateProductId: string) => {
    if (!accessToken || !id) return;

    try {
      const response = await fetch(
        `${API_URL}/api/customer-type-templates/${id}/products/${templateProductId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }

      setProducts((prev) => prev.filter((p) => p.id !== templateProductId));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove product');
    }
  };

  // Filter out products already assigned to this template
  const availableProducts = inventoryItems.filter(
    (item) => !products.some((p) => p.product.id === item.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div>
        <Link
          to="/admin/customer-type-templates"
          className="inline-flex items-center space-x-1 text-amber-500 hover:text-amber-400 text-sm mb-3"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Templates</span>
        </Link>
        <div className="relative">
          <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-500 via-blue-500 to-amber-500" />
          <h1 className="text-3xl font-bold text-slate-100 pl-6">
            {isEdit ? 'Edit Template' : 'New Template'}
          </h1>
          <p className="text-slate-400 text-sm pl-6 mt-1">
            {isEdit
              ? 'Update template details and manage assigned products'
              : 'Create a new customer type template'}
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {/* Template Form */}
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber-500" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber-500" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber-500" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber-500" />

          <div className="bg-slate-800 border border-slate-700 p-6">
            <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4">
              Template Details
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="e.g., Residential, Commercial, Industrial"
                  className="w-full bg-slate-900 border border-slate-700 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 px-3 py-2 text-slate-100 text-sm font-mono placeholder-slate-600"
                  required
                />
              </div>

              <div className="md:col-span-1 flex items-end">
                <label className="flex items-center space-x-3 cursor-pointer pb-2">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          isActive: e.target.checked,
                        }))
                      }
                      className="sr-only"
                    />
                    <div
                      className={`w-10 h-5 rounded-full transition-colors ${
                        formData.isActive ? 'bg-amber-500' : 'bg-slate-600'
                      }`}
                    >
                      <div
                        className={`w-4 h-4 bg-white rounded-full shadow transition-transform mt-0.5 ${
                          formData.isActive
                            ? 'translate-x-5'
                            : 'translate-x-0.5'
                        }`}
                      />
                    </div>
                  </div>
                  <span className="text-sm text-slate-300">
                    {formData.isActive ? 'Active' : 'Inactive'}
                  </span>
                </label>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  rows={3}
                  placeholder="Describe this customer type template..."
                  className="w-full bg-slate-900 border border-slate-700 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 px-3 py-2 text-slate-100 text-sm placeholder-slate-600 resize-none"
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end space-x-3">
              <Link
                to="/admin/customer-type-templates"
                className="px-4 py-2 border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors text-sm"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center space-x-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-medium py-2 px-4 transition-colors text-sm"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span>
                  {saving
                    ? 'Saving...'
                    : isEdit
                    ? 'Update Template'
                    : 'Create Template'}
                </span>
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Products Section (only shown for existing templates) */}
      {isEdit && (
        <div className="relative">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-blue-500" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-blue-500" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-blue-500" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-blue-500" />

          <div className="bg-slate-800 border border-slate-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider">
                Assigned Products ({products.length})
              </h3>
              {!showAddProduct && (
                <button
                  type="button"
                  onClick={() => setShowAddProduct(true)}
                  className="flex items-center space-x-1 text-amber-500 hover:text-amber-400 text-sm transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Product</span>
                </button>
              )}
            </div>

            {/* Add Product Form */}
            {showAddProduct && (
              <div className="bg-slate-900 border border-slate-700 p-4 mb-4">
                <div className="flex items-end space-x-3">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Product
                    </label>
                    <select
                      value={selectedProductId}
                      onChange={(e) => setSelectedProductId(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 px-3 py-2 text-slate-100 text-sm"
                    >
                      <option value="">Select a product...</option>
                      {availableProducts.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name} ({item.sku})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="w-36">
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Default Margin %
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={productMargin}
                      onChange={(e) => setProductMargin(e.target.value)}
                      placeholder="e.g., 15"
                      className="w-full bg-slate-800 border border-slate-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 px-3 py-2 text-slate-100 text-sm font-mono placeholder-slate-600"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleAddProduct}
                    disabled={!selectedProductId || addingProduct}
                    className="flex items-center space-x-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2 px-3 transition-colors text-sm whitespace-nowrap"
                  >
                    {addingProduct ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    <span>Add</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddProduct(false);
                      setSelectedProductId('');
                      setProductMargin('');
                    }}
                    className="px-3 py-2 border border-slate-600 text-slate-400 hover:bg-slate-700 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Products List */}
            {products.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-slate-500 text-sm">
                  No products assigned yet. Click "Add Product" to get started.
                </div>
              </div>
            ) : (
              <table className="w-full">
                <thead className="border-b border-slate-700">
                  <tr>
                    <th className="text-left px-3 py-2 text-slate-400 text-xs uppercase tracking-wider font-medium">
                      Product
                    </th>
                    <th className="text-left px-3 py-2 text-slate-400 text-xs uppercase tracking-wider font-medium">
                      SKU
                    </th>
                    <th className="text-right px-3 py-2 text-slate-400 text-xs uppercase tracking-wider font-medium">
                      Default Margin
                    </th>
                    <th className="text-right px-3 py-2 text-slate-400 text-xs uppercase tracking-wider font-medium w-16">
                      Remove
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((tp) => (
                    <tr
                      key={tp.id}
                      className="border-b border-slate-700/50 hover:bg-slate-900/30"
                    >
                      <td className="px-3 py-3 text-slate-100 text-sm font-medium">
                        {tp.product.name}
                      </td>
                      <td className="px-3 py-3 text-slate-400 text-sm font-mono">
                        {tp.product.sku}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {tp.defaultMargin != null ? (
                          <span className="text-amber-500 text-sm font-mono">
                            {tp.defaultMargin}%
                          </span>
                        ) : (
                          <span className="text-slate-600 text-sm italic">
                            None
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => handleRemoveProduct(tp.id)}
                          className="p-1 text-slate-400 hover:text-red-400 transition-colors"
                          title="Remove product"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
